import { useAuthMe } from '@h2hspark/auth-ui';
import { EmptyState, Skeleton, cn } from '@h2hspark/ui';
import { Printer, ReceiptText } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { formatTicketDateTime, registrantName } from '../event-tickets/constants';

import { useMyOrder } from './api';
import {
  ORDER_NOT_FOUND_DESCRIPTION,
  ORDER_NOT_FOUND_TITLE,
  PRINT_RECEIPT_LABEL,
  RECEIPT_CLOSE_LABEL,
  RECEIPT_BILLED_TO_LABEL,
  RECEIPT_DOCUMENT_HEADING,
  RECEIPT_EVENT_LABEL,
  RECEIPT_MERCHANT_OF_RECORD,
  RECEIPT_PAYMENT_DATE_LABEL,
  SERVICE_FEE_DISCLOSURE_TEST_ID,
  orderDetailPath,
  orderNumberLine,
  savedCardLine,
} from './constants';
import { OrderItemisation } from './order-itemisation-card';

/**
 * **The receipt** (`MEM-022` AC-5, `MEM-TKT-014`) — H2HSpark's own record of one transaction.
 *
 * ## It is a different artefact from `order-receipt-page.tsx`, and the two must not merge
 *
 * `OrderReceiptPage` at `/app/events/:eventId/receipt` is `MEM-020`'s **confirmation**: reached
 * straight after checkout, headed *"Your reservation"*, and it tells a member they have a place.
 * This is the printable document AC-5 describes — the thing a member files for expenses or
 * produces in a dispute. Two artefacts, two headings, one of them addressed by an order id
 * because a receipt outlives the checkout that produced it.
 *
 * ## What AC-5 lists, element by element
 *
 * | AC-5 element | Where it comes from |
 * | --- | --- |
 * | the H2HSpark mark | `/brand/logotype.png`, the same file the site chrome renders |
 * | "Receipt" with the order number | `RECEIPT_DOCUMENT_HEADING` and `order.orderNumber` |
 * | Billed to — name and email | **the session**, via `useAuthMe` |
 * | payment date | `order.payment.paidAt`, in the event's own zone |
 * | card brand and last four | `order.payment.card`, `PAY-001`'s four display values |
 * | Event block — name, date, time, venue | `order.eventName`, `startsAt`, `timezone`, `venueName` |
 * | the itemised lines — ticket, Service fee, Total | `OrderItemisation`, the one itemiser |
 * | the footer | `RECEIPT_MERCHANT_OF_RECORD`, served — see below; and `MEM-050` AC-3's disclosure |
 *
 * **"Billed to" reads the session rather than the payload**, and that is deliberate. The name and
 * address of the person being billed are the reader's own, so serving them on an order payload
 * would put a member's email on a document the API had to be asked for — a field with no reader
 * but this screen, on a route this screen already authenticates to. The ticket names its holder
 * the same way, from `/auth/me`, for the same reason.
 *
 * ## The footer, and the question that is still open
 *
 * `MEM-TKT-014`'s cell is clipped at *"and a footer …"*. The story decided what it carries rather
 * than queuing it — the legal entity as merchant of record, the merchant-of-record sentence, a
 * support address, and **the refund terms snapshotted on this order** rather than the event's
 * current policy. ADR 020 and `SYS-019` make H2HSpark the merchant, so this is H2HSpark's document
 * and it has to say who took the money and what the terms were at the time.
 *
 * The legal entity **line is pending counsel** under `OI-7`; the story says in as many words that
 * this is *"a wording question, not a build one, and the field is there either way"*. It is served
 * from the API, so when counsel answers, one constant changes and no screen does.
 *
 * The prototype's own footer reads *"Thank you for joining us. Full refunds up to 48 hours before
 * the event."* — the second sentence being one organizer's sample policy presented as a platform
 * rule, which is exactly the defect `MEM-TKT-013`'s correction records. What renders here is this
 * order's snapshot, or the served sentence saying there is none.
 *
 * ## "Total", not "Total paid"
 *
 * AC-5 used to end *"the four itemised lines ending 'Total paid'"* while the line reads
 * **`Total`** — the conflict with `MEM-020` AC-3, recorded in three places before this screen
 * existed and left to a PM. **Sumit ruled on 2026-08-29: the label is "Total"**, AC-5 was reworded
 * and is now tagged, and no constant moved. `order-detail-page.tsx`'s header carries the argument
 * in full.
 *
 * ## Printing
 *
 * `window.print()`, and no download. The browser's own dialog produces a PDF on every platform
 * this app runs on, so there is nothing to generate, nothing to store, and no signed URL to leak —
 * and a receipt is exactly the document somebody would otherwise ask us to email.
 *
 * **This comment used to claim the navigation was dropped and it was not.** Only the Print button
 * carried `print:hidden`; the desktop header, the whole purple in-app footer with its four link
 * columns, and the phone tab bar all printed, so what came out of the dialog was a screenshot of
 * the app with a receipt in the middle of it. Found on 2026-09-12 by walking the page for elements
 * outside the sheet, which is the only way to see it short of printing. `member-layout.tsx`,
 * `member-footer.tsx` and `phone-top-bar.tsx` now carry `print:hidden` on the chrome, because the
 * claim is about the shell rather than about this page.
 */
export function OrderReceiptDocumentPage(): React.ReactElement {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const { user } = useAuthMe();
  const query = useMyOrder(orderId, { enabled: true });

  if (query.isPending) {
    return (
      <ReceiptShell orderId={orderId}>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </ReceiptShell>
    );
  }

  if (query.error !== null || query.data === undefined) {
    return (
      <ReceiptShell orderId={orderId}>
        <EmptyState
          icon={<ReceiptText className="h-6 w-6" aria-hidden />}
          title={ORDER_NOT_FOUND_TITLE}
          description={ORDER_NOT_FOUND_DESCRIPTION}
        />
      </ReceiptShell>
    );
  }

  const { order } = query.data;
  // `registrantName` is the ticket's own rule for a display name, reused: trimmed, and `null`
  // rather than a placeholder for an account that has none. One rule, so the ticket and the
  // receipt cannot name one member two ways.
  const billedToName = registrantName(user?.displayName ?? null);
  const when = formatTicketDateTime({ startsAt: order.startsAt, timezone: order.timezone });
  const paidOn =
    order.payment.paidAt === null
      ? null
      : formatTicketDateTime({ startsAt: order.payment.paidAt, timezone: order.timezone });

  return (
    <ReceiptShell orderId={orderId} printable>
      <article
        data-testid="receipt-document"
        className="overflow-hidden bg-white p-8 print:p-0"
      >
        {/* §2.16's own head: the mark at the **left**, and "Receipt" over the order number
            right-aligned opposite it. It was the other way round — a heading at the left and the
            mark at the right — which reads as a page rather than as a document somebody files. */}
        <header className="mb-7 flex items-start justify-between gap-5">
          {/* `aria-hidden` with an empty `alt` because the document is already headed "Receipt"
              and the brand is stated in the footer — a second announcement of it is noise to a
              screen reader (`NFR accessibility#wcag-aa`). */}
          <img
            src="/brand/logotype.png"
            alt=""
            aria-hidden
            className="h-5 w-auto shrink-0"
            width={1787}
            height={298}
          />
          <div className="text-right">
            <h1 className="font-brand text-lg font-black text-foreground">
              {RECEIPT_DOCUMENT_HEADING}
            </h1>
            <p className="text-xs text-muted-foreground">{orderNumberLine(order.orderNumber)}</p>
          </div>
        </header>

        <div className="mb-[26px] grid gap-5 sm:grid-cols-2">
          <section className="space-y-0.5">
            <h2 className={RECEIPT_EYEBROW}>{RECEIPT_BILLED_TO_LABEL}</h2>
            {/* From the session — the reader's own name and address. Dropped rather than replaced
                by a placeholder when the account carries neither. */}
            {billedToName === null ? null : (
              <p className="text-sm font-semibold text-foreground">{billedToName}</p>
            )}
            {user?.emailAddress === undefined ||
            user.emailAddress === null ||
            user.emailAddress.length === 0 ? null : (
              <p className="text-sm text-muted-foreground">{user.emailAddress}</p>
            )}
          </section>

          <section className="space-y-0.5">
            <h2 className={RECEIPT_EYEBROW}>{RECEIPT_PAYMENT_DATE_LABEL}</h2>
            {paidOn === null ? null : (
              <p className="text-sm font-semibold text-foreground">{paidOn}</p>
            )}
            {order.payment.card === null ? null : (
              <p className="text-sm text-muted-foreground">
                {savedCardLine(order.payment.card.brand, order.payment.card.last4)}
              </p>
            )}
          </section>
        </div>

        <h2 className={cn(RECEIPT_EYEBROW, 'mb-2.5')}>{RECEIPT_EVENT_LABEL}</h2>
        {/* §2.16 boxes the event: a `--border-subtle` hairline at `--radius-md` with 16px inside
            it, which is what separates the thing that was bought from the money below. */}
        <section className="mb-[22px] rounded-md border border-border-subtle p-4">
          <p className="font-brand text-md font-bold text-foreground">{order.eventName}</p>
          {/* Date and time on their own line, in the event's zone with its abbreviation, then the
              venue. Two lines rather than one joined string: this is a document rather than a
              list row, and AC-5 names the four parts separately. */}
          {when === null ? null : <p className="mt-0.5 text-sm text-muted-foreground">{when}</p>}
          {order.venueName === null ? null : (
            <p className="text-sm text-muted-foreground">{order.venueName}</p>
          )}
        </section>

        {/*
          The one itemiser, a third time. Nothing in this file maps `order.lines`.

          **The money is ours, not §2.16's.** The prototype writes a `$0.00` service fee and a
          footer promising *"Full refunds up to 48 hours before the event"* — both contradict the
          fee model (`FIN-001`, `MEM-050`) and the terms this order actually agreed, and
          `handoff-corrections.md` already carries them. The layout is the prototype's; every
          figure and every sentence below is the platform's.

          **No `className` any more.** This used to pass `border-0 p-0` to take off a card the
          itemiser drew and a printed page does not want. The itemiser draws no card now — it
          renders the lines and each caller supplies the surface its screen wants, which is what
          this line was arguing for by undoing one.
        */}
        <OrderItemisation order={order.order} />

        <footer className="mt-[26px] space-y-1 border-t border-divider pt-4 text-xs leading-relaxed text-text-subtle">
          <p>{RECEIPT_MERCHANT_OF_RECORD.legalEntity}</p>
          <p>{RECEIPT_MERCHANT_OF_RECORD.statement}</p>
          <p>{RECEIPT_MERCHANT_OF_RECORD.supportAddress}</p>
          {/* This order's own snapshotted terms, never the event's current policy — the reason
              `AgreedRefundPolicy` is a separate type from `MemberRefundPolicy` on the server. */}
          <p>{order.refundPolicy?.sentence ?? order.refundPolicyNotice}</p>
          {/* And the fee-forfeit disclosure beside them, in the served words the member read
              before paying (`MEM-050` AC-3) — on the document they file, because a dispute asks
              what the terms were at the time. */}
          {order.feeDisclosure === null ? null : (
            <p data-testid={SERVICE_FEE_DISCLOSURE_TEST_ID}>{order.feeDisclosure}</p>
          )}
        </footer>
      </article>
    </ReceiptShell>
  );
}

/** §2.16's eyebrow over each block: 9px, uppercase, `.1em`, `--text-subtle`. */
const RECEIPT_EYEBROW =
  'font-brand text-[9px] font-bold uppercase tracking-[0.1em] text-text-subtle';

/**
 * §2.16's sheet, and its bar.
 *
 * The prototype draws the receipt as a 520px centred card with `--radius-xl` and `--shadow-xl`,
 * over a bar carrying **Print** and **Close** and marked `data-receipt-hide` so neither control
 * reaches the paper. That is what this is, with two differences that follow from it being a
 * **route** rather than an overlay:
 *
 *   - **Close is a link to the order detail**, not a state change. A member reaches this address
 *     from Tickets & orders, from a bookmark and from an emailed link, and the useful "close" is
 *     the screen behind it rather than nothing.
 *   - `print:hidden` does the job `data-receipt-hide` does in the prototype's own print handler.
 *     `window.print()` on the page itself is why the route has to stay: a dialog's print is the
 *     page it sits on, and this document has to print alone.
 *
 * The route is unchanged (`MEM-022` AC-5) and still resolves standalone, which is what makes it
 * linkable from an email and printable from a bookmark.
 */
function ReceiptShell({
  orderId,
  printable = false,
  children,
}: {
  readonly orderId: string;
  /** Whether there is a document to print. `false` while loading and for a refused id. */
  readonly printable?: boolean;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="mx-auto w-full max-w-[520px] overflow-hidden rounded-xl bg-white shadow-xl print:max-w-none print:rounded-none print:shadow-none">
      <div className="flex items-center justify-between gap-3 border-b border-divider px-6 py-4 print:hidden">
        <span className="font-brand text-md font-bold text-foreground">
          {RECEIPT_DOCUMENT_HEADING}
        </span>
        <div className="flex gap-2">
          {!printable ? null : (
            <button
              type="button"
              onClick={() => {
                window.print();
              }}
              className="inline-flex items-center gap-[7px] rounded-pill bg-brand px-4 py-[9px] font-brand text-sm font-bold text-primary-foreground"
            >
              <Printer className="h-[15px] w-[15px]" aria-hidden />
              {PRINT_RECEIPT_LABEL}
            </button>
          )}
          <Link
            to={orderDetailPath(orderId)}
            className="inline-flex items-center rounded-pill border-[1.5px] border-border bg-card px-[14px] py-[9px] font-brand text-sm font-bold text-text-body"
          >
            {RECEIPT_CLOSE_LABEL}
          </Link>
        </div>
      </div>
      <div className="p-6 pt-0 print:p-0">{children}</div>
    </div>
  );
}
